import React, { useReducer } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import styled from 'styled-components';

import { Box, TextField, Grid, Typography, CircularProgress } from '@material-ui/core';

import { PRIVACY_POLICY_PATH } from '../../routes';
import { bsSleep } from '../../utils/manualTimeout';
import { postContactFormData } from '../../services/contactService';

import BtnPrimary from '../common/BtnPrimary';
import CheckboxPrimary from '../common/CheckboxPrimary';

const Root = styled.div`
	.contact__footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.contact__data-privacy {
		display: flex;
		align-items: center;
	}

	.contact__success-screen {
		margin: 200px 0;
		text-align: center;

		p:not(:first-child) {
			margin-top: 20px;
		}
	}

	.error {
		margin: 20px 0 10px 0;
		padding: 20px;
		background-color: ${(props) => props.theme.palette.background.error};
		color: ${(props) => props.theme.palette.text.primary};
		font-size: 16px;
		border-radius: 5px;
	}

	.registerViewLinks {
		text-decoration: none;
		color: ${(props) => props.theme.palette.bscolors.bshellblau01};

		&:hover {
			text-decoration: underline;
			cursor: pointer;
		}
	}
`;

function contactFormReducer(state, action) {
	switch (action.type) {
		case 'field': {
			return {
				...state,
				[action.fieldName]: action.payload,
			};
		}

		case 'pushingData': {
			return {
				...state,
				isLoading: true,
			};
		}

		case 'success': {
			return {
				...state,
				isDelivered: true,
			};
		}
		case 'error': {
			return {
				...state,
				error: action.error,
				isDelivered: false,
			};
		}
		default:
			return state;
	}
}

const initialState = {
	username: '',
	company: '',
	phoneNumber: '',
	message: '',
	subject: '',
	email: '',
	isDelivered: false,
	error: '',
	isLoading: false,
	onChangeConfirmDataPrivacy: false,
};

export default function FormContact() {
	const [state, dispatch] = useReducer(contactFormReducer, initialState);

	const {
		username,
		company,
		phoneNumber,
		subject,
		message,
		email,
		isDelivered,
		error,
		isLoading,
	} = state;

	const onSubmit = async (e) => {
		e.preventDefault();

		const data = JSON.stringify({
			username,
			company,
			email,
			phoneNumber,
			subject,
			message,
		});

		dispatch({ type: 'pushingData' });
		await bsSleep(2000);
		try {
			await postContactFormData(data);
			dispatch({ type: 'success' });
		} catch (error) {
			dispatch({ type: 'error', error: error.message });
			console.log('error signing up:', error);
		}
	};

	const onChange = (e) => {
		dispatch({
			type: 'field',
			fieldName: e.target.name,
			payload: e.target.checked,
		});
	};

	const dataPrivacy = (
		<RouterLink target="_blank" to={PRIVACY_POLICY_PATH}>
			Datenschutzerklärung
		</RouterLink>
	);

	const SuccessScreen = (
		<div className="contact__success-screen">
			<Typography>
				Vielen Dank für Ihre Kontaktanfrage. Wir werden uns schnellstmöglich bei
				Ihnen melden.
			</Typography>
			<Typography>Ihr be.storaged-Team</Typography>
		</div>
	);

	return (
		<Root>
			<form onSubmit={onSubmit}>
				{error && (
					<div className="error">
						<span>{error}</span>
					</div>
				)}
				{isDelivered ? (
					SuccessScreen
				) : (
					<>
						<Grid container>
							<Grid item xs={6}>
								<Box mr={3}>
									<TextField
										value={username}
										variant="outlined"
										margin="normal"
										required
										fullWidth
										label="Benutzername"
										onChange={(e) =>
											dispatch({
												type: 'field',
												fieldName: 'username',
												payload: e.currentTarget.value,
											})
										}
									/>
								</Box>
							</Grid>
							<Grid item xs={6}>
								<Box ml={3}>
									<TextField
										value={company}
										variant="outlined"
										margin="normal"
										required
										fullWidth
										label="Firma"
										onChange={(e) =>
											dispatch({
												type: 'field',
												fieldName: 'company',
												payload: e.currentTarget.value,
											})
										}
									/>
								</Box>
							</Grid>

							<Grid item xs={6}>
								<Box mr={3}>
									<TextField
										value={email}
										variant="outlined"
										margin="normal"
										type="email"
										required
										fullWidth
										label="E-Mail-Adresse"
										onChange={(e) =>
											dispatch({
												type: 'field',
												fieldName: 'email',
												payload: e.currentTarget.value,
											})
										}
									/>
								</Box>
							</Grid>

							<Grid item xs={6}>
								<Box ml={3}>
									<TextField
										value={phoneNumber}
										variant="outlined"
										margin="normal"
										type="tel"
										fullWidth
										label="Telefonnummer"
										onChange={(e) =>
											dispatch({
												type: 'field',
												fieldName: 'phoneNumber',
												payload: e.currentTarget.value,
											})
										}
									/>
								</Box>
							</Grid>

							<Grid item xs={12}>
								<TextField
									value={subject}
									variant="outlined"
									margin="normal"
									required
									fullWidth
									label="Betreff"
									onChange={(e) =>
										dispatch({
											type: 'field',
											fieldName: 'subject',
											payload: e.currentTarget.value,
										})
									}
								/>
							</Grid>
							<Grid item xs={12}>
								<TextField
									style={{ minHeight: '200px' }}
									value={message}
									variant="outlined"
									margin="normal"
									required
									fullWidth
									label="Ihre Nachricht"
									multiline
									inputProps={{
										style: { minHeight: '140px' },
									}}
									onChange={(e) =>
										dispatch({
											type: 'field',
											fieldName: 'message',
											payload: e.currentTarget.value,
										})
									}
								/>
							</Grid>
						</Grid>
						<div className="contact__footer">
							<div className="contact__data-privacy">
								<CheckboxPrimary
									required
									onChange={onChange}
									name="onChangeConfirmDataPrivacy"
								/>
								<Typography variant="body2">
									Ich akzeptiere die {dataPrivacy}*
								</Typography>
							</div>
							<BtnPrimary
								type="submit"
								variant="contained"
								disabled={isLoading}
							>
								{isLoading ? 'Versende Formular...' : 'Absenden'}
								{isLoading && (
									<CircularProgress
										size={24}
										style={{ marginLeft: 20 }}
									/>
								)}
							</BtnPrimary>
						</div>
					</>
				)}
			</form>
		</Root>
	);
}
